import { Checkbox } from 'antd';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';
import { UZ_PHONE_MASK } from '@/shared/constants';
import { Button, Input, MaskedInput } from '@/shared/ui';

import type { ClientFormValues } from '../../types/client-form';

interface IProps {
  methods: UseFormReturn<ClientFormValues, unknown, ClientFormValues>;
  handleClose: () => void;
  clientFormLoading: boolean;
  clientError: boolean;
  handleClientFormSubmit: (values: ClientFormValues) => Promise<void>;
  mainAgreements: {
    policyLink: string;
    agreementLink: string;
  };
}

const ClientForm: React.FC<IProps> = ({
  methods,
  handleClose,
  clientFormLoading,
  clientError,
  handleClientFormSubmit,
  mainAgreements
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    handleSubmit
  } = methods;

  const onSubmit = (values?: ClientFormValues) => {
    if (!values) return;
    handleClientFormSubmit(values);
  };

  return (
    <form className="client-otp-modal__form" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="personalId"
        rules={{
          required: t('please_enter_data'),
          minLength: {
            value: 14,
            message: t('invalid_pinfl')
          }
        }}
        render={({ field }) => (
          <Input
            {...field}
            inputMode="numeric"
            label={t('pinfl')}
            value={field.value ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value.match(/\D/) || value.length > 14) return;
              field.onChange(value);
            }}
            error={errors.personalId?.message}
            isRequired
          />
        )}
      />
      <Controller
        control={control}
        name="fullName"
        rules={{
          required: t('please_enter_data'),
          minLength: {
            value: 1,
            message: t('please_enter_data')
          }
        }}
        render={({ field }) => (
          <Input
            {...field}
            label={t('full_name')}
            value={field.value ?? ''}
            error={errors.fullName?.message}
            isRequired
          />
        )}
      />
      <Controller
        control={control}
        name="phone"
        rules={{
          required: t('please_enter_data'),
          validate: (value) => {
            if (!value.match(/\+\d\d\d \(\d\d\) \d\d\d-\d\d-\d\d/))
              return t('invalid_phone');
          }
        }}
        render={({ field }) => (
          <MaskedInput
            {...field}
            type="tel"
            label={t('phone_number')}
            value={field.value ?? ''}
            error={errors.phone?.message}
            mask={UZ_PHONE_MASK}
            isRequired
            autoComplete="off"
          />
        )}
      />

      {clientError && (
        <p className="client-otp-modal__error">
          <AlertCircleIcon />
          {t('error_try_later')}
        </p>
      )}

      <Controller
        control={control}
        name="policyAgreement"
        rules={{
          required: t('agree_terms_required')
        }}
        render={({ field, fieldState: { error } }) => (
          <div>
            <Checkbox
              {...field}
              checked={field.value}
              onChange={(e) => {
                field.onChange(e.target.checked);
              }}
            >
              {t('agree_terms_prefix')}{' '}
              <a
                className="client-otp-modal__link"
                href={mainAgreements.policyLink}
                target="_blank"
              >
                {t('privacy_policy')}
              </a>
              ,{' '}
              <a
                className="client-otp-modal__link"
                href={mainAgreements.agreementLink}
                target="_blank"
              >
                {t('user_agreement')}
              </a>{' '}
              {t('agree_terms_suffix')}
            </Checkbox>
            {error && (
              <p className="client-otp-modal__field-error">{error.message}</p>
            )}
          </div>
        )}
      />

      <div className="client-otp-modal__actions">
        <Button type="button" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button
          disabled={Object.values(errors).length !== 0}
          variant="filled-blue"
          isLoading={clientFormLoading}
        >
          {t('continue')}
        </Button>
      </div>
    </form>
  );
};

export default ClientForm;
